import React, { useState, useEffect } from 'react';
import { formatPhoneNumber } from '../../utils/formatters';
import { getJobTypeCommission } from '../../utils/jobTypeCommission';
import CustomerCombobox from './CustomerCombobox';

const CreateInformationTab = ({
  status, setStatus, attempted,
  selectedCustomerId, handleCustomerChange, customers, loadingCustomers,
  phone, setPhone,
  territory, setTerritory,
  franchises,
  projectType, setProjectType, projectTypes = [],
  address, setAddress, customerAddresses, handleAddressChange,
  projectSource, setProjectSource, projectSources = [],
  fnolDate, setFnolDate,
  estimateDate, setEstimateDate,
  startDate, setStartDate,
  completionDate, setCompletionDate,
  preTaxTotalValue, setPreTaxTotalValue,
  placeholder, setPlaceholder,
  paymentType, setPaymentType,
  claimNumber, setClaimNumber,
  invoiceNumber, setInvoiceNumber,
  paidInFull, setPaidInFull,
  mgmtBonusPaid, setMgmtBonusPaid,
  projectPaidDate, setProjectPaidDate,
  restorationOpp, setRestorationOpp,
  nonCommissionProject, setNonCommissionProject,
  authorizationSigned, setAuthorizationSigned,
  uploadRequired, setUploadRequired,
  getCustomerDisplayName,
  typeCommissionMap = {},
}) => {
  // Local input text so the user can type freely; resets to the selected
  // customer's display name when they pick one (or when the parent changes
  // selectedCustomerId from outside).
  const selectedCustomer = customers.find((c) => c.customer_id === selectedCustomerId);
  const [customerInput, setCustomerInput] = useState(selectedCustomer ? getCustomerDisplayName(selectedCustomer) : '');
  useEffect(() => {
    if (selectedCustomerId && selectedCustomer) {
      setCustomerInput(getCustomerDisplayName(selectedCustomer));
    }
  }, [selectedCustomerId, customers]); // eslint-disable-line react-hooks/exhaustive-deps
  const typeCommission = getJobTypeCommission(typeCommissionMap, projectType);

  return (
    <>
      {/* Header with Status */}
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-2xl text-[#5B8DB8] font-light">Project Information</h2>
        <div className="flex items-center gap-2">
          <label className="text-red-500 text-sm">Status:</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={`border rounded px-3 py-2 w-48 ${attempted && !status ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            data-testid="project-status-select"
          >
            <option value="">-- select --</option>
            <option value="Accounts Receivable">Accounts Receivable</option>
            <option value="Check-In">Check-In</option>
            <option value="Closed">Closed</option>
            <option value="Completed">Completed</option>
            <option value="Dead">Dead</option>
            <option value="Estimate">Estimate</option>
            <option value="In Progress">In Progress</option>
            <option value="On-Hold">On-Hold</option>
            <option value="Scheduled">Scheduled</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8">
        {/* Left Column */}
        <div>
          {/* Basics Section */}
          <h3 className="text-lg text-gray-700 mb-4 italic">Basics</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Customer:</label>
              <CustomerCombobox
                customers={customers}
                value={customerInput}
                onChange={(text) => {
                  setCustomerInput(text);
                  // If the user clears the input or types something that no
                  // longer matches the selected customer, clear the selection.
                  if (selectedCustomerId) {
                    const cur = customers.find((c) => c.customer_id === selectedCustomerId);
                    if (!cur || getCustomerDisplayName(cur) !== text) {
                      handleCustomerChange('');
                    }
                  }
                }}
                onCustomerSelect={(cust) => handleCustomerChange(cust.customer_id)}
                getDisplayName={getCustomerDisplayName}
                placeholder={loadingCustomers ? 'Loading...' : 'Type a customer name...'}
                disabled={loadingCustomers}
                invalid={attempted && !selectedCustomerId}
                testId="project-customer"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-32 text-right">Phone:</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                placeholder="215-555-1234"
                className="border border-gray-300 rounded px-3 py-2 flex-1"
                data-testid="project-phone-input"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Territory:</label>
              <select
                value={territory}
                onChange={(e) => setTerritory(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1"
              >
                <option value="">-- select --</option>
                {(franchises || []).map((f) => (
                  <option key={f.number} value={`${f.name} (${f.number})`}>
                    {f.name} ({f.number})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Type:</label>
              <select
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                className={`border rounded px-3 py-2 flex-1 ${attempted && !projectType ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                data-testid="project-type-select"
              >
                <option value="">-- select --</option>
                {projectTypes.map(typeName => (
                  <option key={typeName} value={typeName}>{typeName}</option>
                ))}
              </select>
            </div>
            {projectType && typeCommission !== undefined && (
              <div className="flex items-start gap-4" data-testid="type-commission-hint">
                <span className="w-32"></span>
                {nonCommissionProject ? (
                  <p className="text-xs text-gray-500 italic flex-1">
                    Non-Commission Project is checked — commission for <strong>{projectType}</strong> is not applied to Expenses.
                  </p>
                ) : typeCommission > 0 ? (
                  <p className="text-xs text-[#5B8DB8] flex-1">
                    <strong>{projectType}</strong> adds a <strong>{typeCommission}%</strong> commission to Expenses.
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 italic flex-1">
                    <strong>{projectType}</strong> has no commission rate configured.
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Address:</label>
              {customerAddresses.length > 0 ? (
              <select
                value={address}
                onChange={(e) => handleAddressChange(e.target.value)}
                className={`border rounded px-3 py-2 flex-1 ${attempted && !address ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                data-testid="project-address-select"
              >
                <option value="">-- select --</option>
                {customerAddresses.map((addr, index) => {
                  const fullAddress = `${addr.street_address || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zip || ''}`.trim();
                  return (
                    <option key={index} value={fullAddress}>
                      {fullAddress}
                    </option>
                  );
                })}
              </select>
              ) : (
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter job site address"
                className={`border rounded px-3 py-2 flex-1 ${attempted && !address ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                data-testid="project-address-input"
              />
              )}
            </div>

            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Project Source:</label>
              <select
                value={projectSource}
                onChange={(e) => setProjectSource(e.target.value)}
                className={`border rounded px-3 py-2 flex-1 ${attempted && !projectSource ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                data-testid="project-source-select"
              >
                <option value="">-- select --</option>
                {projectSources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Key Dates Section */}
          <h3 className="text-lg text-gray-700 mb-4 mt-8 italic">Key Dates</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">FNOL Date:</label>
              <input
                type="date"
                value={fnolDate}
                onChange={(e) => setFnolDate(e.target.value)}
                className={`border rounded px-3 py-2 flex-1 ${attempted && !fnolDate ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                data-testid="project-fnol-date"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-32 text-right">Estimate Date:</label>
              <input
                type="date"
                value={estimateDate}
                onChange={(e) => setEstimateDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-32 text-right">Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className={`text-sm w-32 text-right ${['Accounts Receivable', 'Closed', 'Completed'].includes(status) ? 'text-red-500' : ''}`}>Completion Date:</label>
              <input
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                className={`border rounded px-3 py-2 flex-1 ${['Accounts Receivable', 'Closed', 'Completed'].includes(status) && attempted && !completionDate ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div>
          {/* Financial Section */}
          <h3 className="text-lg text-gray-700 mb-4 italic">Financial</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Pre-Tax Total Value ($):</label>
              <input
                type="text"
                value={preTaxTotalValue}
                onChange={(e) => setPreTaxTotalValue(e.target.value)}
                onBlur={(e) => {
                  const raw = String(e.target.value).trim();
                  if (!raw) return;
                  const num = parseFloat(raw.replace(/[$,]/g, ''));
                  if (!Number.isNaN(num)) setPreTaxTotalValue(num.toFixed(2));
                }}
                className="border border-gray-300 rounded px-3 py-2 flex-1"
              />
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={placeholder}
                  onChange={(e) => setPlaceholder(e.target.checked)}
                />
                Placeholder
              </label>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Payment Type:</label>
              <select
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1"
              >
                <option value="">-- select --</option>
                <option value="Claim">Claim</option>
                <option value="COI">COI</option>
                <option value="Direct Pay">Direct Pay</option>
              </select>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Claim Number:</label>
              <input
                type="text"
                value={claimNumber}
                onChange={(e) => setClaimNumber(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 flex-1"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Invoice Number:</label>
              <div className="flex items-center gap-3 flex-1">
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
                <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={paidInFull}
                    onChange={(e) => setPaidInFull(e.target.checked)}
                  />
                  Paid in Full
                </label>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Mgmt Bonus Paid:</label>
              <input
                type="date"
                value={mgmtBonusPaid}
                onChange={(e) => setMgmtBonusPaid(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-40"
              />
              <div className="relative group">
                <span className="text-blue-500 cursor-help">&#9432;</span>
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <h4 className="text-[#5B8DB8] text-lg font-light mb-2">Mgmt Commission Paid</h4>
                  <p className="text-gray-600 text-sm">The date the bonus was paid to an individual. This will allow the project to come off the bonus report.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className={`text-sm w-40 text-right ${(status === 'Closed' || status === 'Completed') ? 'text-red-500' : ''}`}>Project Paid Date:</label>
              <input
                type="date"
                value={projectPaidDate}
                onChange={(e) => setProjectPaidDate(e.target.value)}
                className={`border rounded px-3 py-2 w-40 ${(status === 'Closed' || status === 'Completed') && attempted && !projectPaidDate ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
            </div>
          </div>

          {/* Project Options Section */}
          <h3 className="text-lg text-gray-700 mb-4 mt-8 italic">Project Options</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Restoration Opp:</label>
              <select
                value={restorationOpp}
                onChange={(e) => setRestorationOpp(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-24"
              >
                <option value="">-- select --</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <div className="relative group">
                <span className="text-blue-500 cursor-help">&#9432;</span>
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <h4 className="text-[#5B8DB8] text-lg font-light mb-2">Restoration Opp</h4>
                  <p className="text-gray-600 text-sm">Identify if Repairs opportunity exist post mitigation services</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Non-Commission Project:</label>
              <input
                type="checkbox"
                checked={nonCommissionProject}
                onChange={(e) => setNonCommissionProject(e.target.checked)}
              />
              <div className="relative group">
                <span className="text-blue-500 cursor-help">&#9432;</span>
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <h4 className="text-[#5B8DB8] text-lg font-light mb-2">Non-Commission Project</h4>
                  <p className="text-gray-600 text-sm">Checked if project is non-commission, when commision % values are set to zero not required.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Authorization Signed:</label>
              <input
                type="checkbox"
                checked={authorizationSigned}
                onChange={(e) => setAuthorizationSigned(e.target.checked)}
              />
              <div className="relative group">
                <span className="text-blue-500 cursor-help">&#9432;</span>
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <h4 className="text-[#5B8DB8] text-lg font-light mb-2">Authorization Signed</h4>
                  <p className="text-gray-600 text-sm">Verification all required documentation was signed by the customer</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Upload Required:</label>
              <input
                type="checkbox"
                checked={uploadRequired}
                onChange={(e) => setUploadRequired(e.target.checked)}
              />
              <div className="relative group">
                <span className="text-blue-500 cursor-help">&#9432;</span>
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                  <h4 className="text-[#5B8DB8] text-lg font-light mb-2">Upload Required</h4>
                  <p className="text-gray-600 text-sm">Identify any projects requiring corporate uploading</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateInformationTab;
